import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AnalyticsProvider } from '@wbk/analytics';
import { AuthProvider } from '@wbk/auth';
import { WbkConfigProvider } from '@wbk/config';
import { UiProvider } from '@wbk/ui';
import Layout from './components/layout';
import ErrorBoundary from './components/error/ErrorBoundary';
import { POSTER_1X1_FADED, POSTER_3X1_FADED } from './constants/fallbackLogo';
import ScrollToTop from './components/common/ScrollToTop';
import useLocalization from './context/localization/useLocalization';
import CookieConsent from './components/common/CookieConsent';
import LocalizationProvider from './context/localization/provider';
import { languages } from './i18n/config';
import { gtmTracker } from '../trackers.config';

const AppLayout = () => {
  return (
    <ErrorBoundary>
      <LocalizationProvider>
        <Providers>
          <ScrollToTop />
          <Layout />
          <CookieConsent />
        </Providers>
      </LocalizationProvider>
    </ErrorBoundary>
  );
};

const Providers = ({ children }: { children?: React.ReactNode }) => {
  const { locale } = useLocalization();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  return (
    <ErrorBoundary>
      <WbkConfigProvider
        i18n={{ useTranslation, Trans, languages }}
        subscriptions={{ active: false }}
        router={{
          locale,
          Link,
          push: navigate,
          replace: (u: string) => navigate(u, { replace: true }),
          params: params as Record<string, string>,
        }}
      >
        <UiProvider imgFallbacks={{ '1x1': POSTER_1X1_FADED, '3x1': POSTER_3X1_FADED }}>
          <AnalyticsProvider trackers={[gtmTracker]} currentLocation={pathname}>
            <AuthProvider autoInit={false}>{children}</AuthProvider>
          </AnalyticsProvider>
        </UiProvider>
      </WbkConfigProvider>
    </ErrorBoundary>
  );
};

export default AppLayout;
